import {
  array,
  discriminatedUnion,
  enum as enum_,
  literal,
  object,
  optional,
  string,
  type ZodError,
} from 'zod';

import { QuizSchema } from './quiz';

/**
 * 퀴즈 아이템의 출처 메타데이터.
 * - 'ai': AI 생성. generationId가 동반되어 ClickHouse 분석 시 server biz_generation_assignment와 join됨
 * - 'manual': 수동 작성
 * - 'duplicate': quiz_set 복제로 발생
 * - 'similar': AI 유사 문제 생성
 */
const QuizItemOriginSchema = object({
  type: enum_(['ai', 'manual', 'duplicate', 'similar']),
  generationId: optional(string()),
});

export const QuizItemFormSchema = QuizSchema.Base.and(
  object({
    subjectiveData: QuizSchema.Subjective,
    multipleData: QuizSchema.Multiple,
    oxData: QuizSchema.OX,
    essayData: QuizSchema.Essay,
    origin: optional(QuizItemOriginSchema),
  }),
);

export const AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE =
  'At least one quiz is required';

export const BuildQuizListFormSchema = object({
  quizList: array(QuizItemFormSchema).min(
    1,
    AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE,
  ),
});

export const getBuildQuizListSchema = () => {
  return BuildQuizListFormSchema;
};

export type BuildQuizListItem = Zod.infer<typeof QuizItemFormSchema>;

export const ValidateQuizListSchema = array(
  QuizSchema.Base.and(
    discriminatedUnion('type', [
      object({
        type: literal('subjective'),
        subjectiveData: QuizSchema.Subjective,
      }),
      object({
        type: literal('multiple'),
        multipleData: QuizSchema.Multiple,
      }),
      object({
        type: literal('ox'),
        oxData: QuizSchema.OX,
      }),
      object({
        type: literal('essay'),
        essayData: QuizSchema.Essay,
      }),
    ]),
  ),
).min(1, AT_LEAST_ONE_QUIZ_REQUIRED_MESSAGE);

export const validateQuizList = (
  quizList: BuildQuizListItem[],
): {
  success: boolean;
  error: ZodError | undefined;
} => {
  let success = true;
  let error: ZodError | undefined = undefined;

  const { success: validateSuccess, error: validateError } =
    ValidateQuizListSchema.safeParse(quizList);

  if (!validateSuccess) {
    success = false;
    error = validateError;
  }

  return { success, error };
};

export const validateQuizItem = (
  quizItem: BuildQuizListItem,
): {
  success: boolean;
  error: ZodError | undefined;
} => {
  const itemSchema = QuizSchema.Base.and(
    discriminatedUnion('type', [
      object({
        type: literal('subjective'),
        subjectiveData: QuizSchema.Subjective,
      }),
      object({
        type: literal('multiple'),
        multipleData: QuizSchema.Multiple,
      }),
      object({
        type: literal('ox'),
        oxData: QuizSchema.OX,
      }),
      object({
        type: literal('essay'),
        essayData: QuizSchema.Essay,
      }),
    ]),
  );

  const result = itemSchema.safeParse(quizItem);

  return {
    success: result.success,
    error: result.success ? undefined : result.error,
  };
};
